import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PaymentFormValues } from 'src/pages/payment-msi/PaymentMsiV2/PaymentMsiV2';
import { FormRow } from 'src/pages/payment-msi/PaymentMsiV2/components/FormRow';
import {
  PaymentSection,
  PaymentSectionHeader,
} from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentSection';
import { PaymentTextField } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentTextField';
import { CheckCircleIcon } from 'src/pages/payment-msi/PaymentMsiV2/icons/CheckCircleIcon';
import { InterestedParty } from 'src/pages/payment-msi/PaymentMsiV2/types/interested-party.type';
import { isInterestedPartySwitchEnabled } from 'src/pages/payment-msi/PaymentMsiV2/utils/is-interested-party-switch-enabled.util';
import { AppTheme } from 'src/themes/muiTheme';

const interestedPartyStyles = {
  collapseContainer: {
    marginTop: '24px',
  },
  landlordInfo: {
    borderRadius: '8px',
    padding: '14px 16px',
    display: 'flex',
    columnGap: '12px',
    marginBottom: '40px',
  },
};

interface InterestedPartySectionProps {
  landlordData: InterestedParty | null;
}

export function InterestedPartySection({
  landlordData,
}: InterestedPartySectionProps) {
  const {
    formState: { errors, defaultValues },
  } = useFormContext<PaymentFormValues>();

  const isLandlord = Boolean(landlordData);
  const [isSwitchActive, setIsSwitchActive] = useState(
    isInterestedPartySwitchEnabled({
      defaultInterestedParty: defaultValues?.interestedParty,
      isLandlord,
    }),
  );
  const isMobile = useMediaQuery<AppTheme>((theme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    return theme.breakpoints.down(mobileMaxSize);
  });

  useEffect(() => {
    const hasErrors = errors.interestedParty;
    if (hasErrors) {
      setIsSwitchActive(true);
    }
  }, [errors]);

  function handleSwitchChange() {
    setIsSwitchActive((prevState) => !prevState);
  }

  const theme = useTheme();
  const showLandlordInfo = landlordData && !isMobile;

  return (
    <PaymentSection>
      <PaymentSectionHeader
        hasSwitch={true}
        sectionTitle={'Add interested party'}
        onSwitchChange={handleSwitchChange}
        switchOpen={isSwitchActive}
        desktopTooltipText={
          'The interested party will be notified of changes to your policy. Usually your landlord or property manager.'
        }
        mobileDescription={'I want to add interested party information'}
      />
      <Collapse in={isSwitchActive} timeout={'auto'}>
        <Box sx={interestedPartyStyles.collapseContainer}>
          {showLandlordInfo ? (
            <Box
              border={`1px solid ${theme.palette.success.main}`}
              sx={interestedPartyStyles.landlordInfo}
            >
              <Box>
                <CheckCircleIcon />
              </Box>
              <Box>
                <Typography variant="body1">
                  {landlordData.fullName} has already been automatically added
                  as an Interested Party to this Policy. Your landlord will
                  receive a confirmation email once the policy has been
                  purchased.
                </Typography>
              </Box>
            </Box>
          ) : null}
          <FormRow>
            <PaymentTextField
              name="interestedParty.fullName"
              label={'LLC / Landlord Name'}
              required={true}
              disabled={isLandlord}
              error={Boolean(errors.interestedParty?.fullName)}
              errorText={errors.interestedParty?.fullName?.message}
            />
            <PaymentTextField
              name="interestedParty.email"
              label={'Email Address'}
              required={true}
              type={'email'}
              disabled={isLandlord}
              error={Boolean(errors.interestedParty?.email)}
              errorText={errors.interestedParty?.email?.message}
            />
          </FormRow>
        </Box>
      </Collapse>
    </PaymentSection>
  );
}
